/* Data (ORDER_TYPES, EXTRA_SEAT_*, PATTERNS, COLORS) loaded from data.js */

/* ============================================================
   STATE
   ============================================================ */
const state = {
  openSection: 'vehicle',
  vehicle: { make: '', model: '', year: '', trim: '', frontSeats: '', backSeats: '', notListed: false },
  orderType: null,
  addExtraSeats: false,
  extraSeatQty: 1,
  addJumpSeat: false,
  jumpAuto: false,     // true when the jump seat was switched on by a 40/20/40 front seat
  jumpUserOff: false,  // true when the customer switched it off themselves
  pattern: null,
  colors: [],
  colorSlot: 'main', // which colour a swatch tap replaces: 'main' or 'second'
  pulseSecond: false, // true only for the render right after the first main pick: "+ Add a 2nd color" pulses
  errors: {}, // { sectionId: 'message' } — populated by validateAll() on Order Now
  agreed: false, // the shipping policy / terms of service checkbox, required before Order Now (same as the product page)
  // "How would you like us to verify your seats?" (Jesse, 2026-09-28): 'photos' | 'config' | 'vin'. Answers for the
  // other methods are kept, so switching back and forth loses nothing; only the chosen method goes on the order.
  verify: { method: null, vin: '', uploadId: null, photos: [], pickerOpen: false } // photos: { key, thumb, status, name, error }
};

/* ============================================================
   RENDER AVAILABILITY + PREVIEW
   Reads window.RENDER_MANIFEST (renders/manifest.js), generated by
   FCC Render Batch/build_web_renders.js. A combo is listed only when all
   three views exist on disk, so a selection can never land on a
   half-rendered state. Re-run that script after each render batch.
   ============================================================ */
function manifest() { return window.RENDER_MANIFEST || null; }

// Shown before any colour is chosen. Matches the old static seat-full.png
// colourway, but uses the real composited renders so the opening image is
// consistent with every other image in the builder — and so the Coverage
// buttons visibly do something before a colour has been picked.
const DEFAULT_PREVIEW = { main: 'Peanut Butter', secondary: 'Sand' };

// Is this exact main+secondary pair rendered? Secondary defaults to main
// (a solid), which is how the manifest keys single-colour selections.
// The pattern whose renders the preview shows: the chosen one, else the manifest default (Diamond).
function currentPattern() {
  const m = manifest();
  return state.pattern || (m && m.defaultPattern) || 'Diamond';
}

function comboAvailable(mainName, secName, pattern = currentPattern()) {
  const m = manifest();
  if (!m || !mainName || !m.patterns || !m.patterns[pattern]) return false;
  return m.patterns[pattern].available[`${mainName}_${secName || mainName}`] === true;
}

// Which secondaries can pair with the currently selected main. Used to grey
// out the rest. With no main chosen yet, nothing is greyed.
function secondaryAvailable(candidateName) {
  const main = state.colors[0];
  if (!main) return true;
  if (candidateName === main.name) return true; // the solid is always the main itself
  return comboAvailable(main.name, candidateName);
}

// Resolve the preview image for the current selection, or null to fall back
// to the generic seat-full.png. Diamond and Basic both have full render sets.
function previewSrc() {
  const m = manifest();
  if (!m) return null;
  const pattern = currentPattern();
  if (!m.patterns || !m.patterns[pattern]) return null;
  // No colour chosen yet: fall back to the default colourway rather than the
  // old AI still, so Coverage still swaps the view from the very first click.
  const mainName = state.colors[0] ? state.colors[0].name : DEFAULT_PREVIEW.main;
  const secName = state.colors[0]
    ? (state.colors[1] ? state.colors[1].name : state.colors[0].name)
    : DEFAULT_PREVIEW.secondary;
  const main = { name: mainName };
  const sec = secName;
  if (!comboAvailable(main.name, sec)) return null;
  const folder = main.name === sec ? main.name : `${main.name}_${sec}`;
  // Coverage drives which of the three views shows; default to the full set.
  const view = m.views[state.orderType] || m.views['Full Set (Front + Back)'];
  // Front Middle Jump Seat: fronts and full set have a variant with the jump seat (in the main colour)
  // between the front seats, built by FCC Render Batch/build_web_jump.js. Backs Only has no front row.
  const jump = state.addJumpSeat && (view === 'fronts' || view === 'full');
  return renderUrl(pattern, main.name, sec, view, jump);
}

// URL of one render. Live store: Shopify Files with flat names, uploaded by FCC Render Batch/shopify/upload_files.js
// (fcc-<pattern>--<combo>--<view>.webp, lowercase, spaces as dashes). Locally: renders/<Pattern>/<Combo>/<view>.webp.
function renderUrl(pattern, mainName, secName, view, jump) {
  const folder = mainName === secName ? mainName : `${mainName}_${secName}`;
  const file = `${view}${jump ? '_jump' : ''}`;
  // Images re-made after upload carry a revision number (manifest.revisions) and live under a new name, because
  // Shopify Files are cached for a year.
  const m = manifest() || {};
  const rev = m.revisions && m.revisions[`${pattern}/${folder}/${file}`];
  if (window.FCC_DESIGNER && window.FCC_DESIGNER.renderBase) {
    const slug = s => s.toLowerCase().replace(/ /g, '-');
    return `${window.FCC_DESIGNER.renderBase}fcc-${slug(pattern)}--${slug(folder)}--${file}${rev ? '--r' + rev : ''}.webp`;
  }
  return `${m.base}/${pattern}/${encodeURIComponent(folder)}/${file}.webp${rev ? '?r=' + rev : ''}`;
}

/* ===== Image prefetching: make preview changes instant =====
   The preview used to wait for each new image to download after a click (1-2s on a first CDN request).
   Now likely-next images are fetched ahead of time into the browser cache:
   - every other view of the colourway on screen (coverage and jump seat switches are instant);
   - a colourway as soon as a swatch or pattern is hovered (desktop) or touched (phone), before the tap completes;
   - on desktop with a good connection, the current view for every solid colour, while the page is idle.
   A small queue keeps it to 3 downloads at a time so it never competes with the image actually being shown. */
const _prefetched = new Set();
const _prefetchQueue = [];
let _prefetchActive = 0;
function prefetch(url, urgent) {
  if (!url || _prefetched.has(url)) return;
  _prefetched.add(url);
  if (urgent) _prefetchQueue.unshift(url); else _prefetchQueue.push(url);
  _pumpPrefetch();
}
function _pumpPrefetch() {
  while (_prefetchActive < 3 && _prefetchQueue.length) {
    const url = _prefetchQueue.shift();
    _prefetchActive++;
    const img = new Image();
    img.decoding = 'async';
    img.onload = img.onerror = () => { _prefetchActive--; _pumpPrefetch(); };
    img.src = url;
  }
}
function selectionParts() {
  const mainName = state.colors[0] ? state.colors[0].name : DEFAULT_PREVIEW.main;
  const secName = state.colors[0] ? (state.colors[1] ? state.colors[1].name : state.colors[0].name) : DEFAULT_PREVIEW.secondary;
  const m = manifest();
  const view = (m && m.views[state.orderType]) || 'full';
  return { pattern: currentPattern(), mainName, secName, view, jump: state.addJumpSeat && (view === 'fronts' || view === 'full') };
}
// Every view of the colourway currently on screen, so switching coverage or the jump seat never waits.
function prefetchOtherViews() {
  const p = selectionParts();
  if (!comboAvailable(p.mainName, p.secName, p.pattern)) return;
  for (const [v, j] of [['full', false], ['fronts', false], ['backs', false], ['fronts', true], ['full', true]]) {
    prefetch(renderUrl(p.pattern, p.mainName, p.secName, v, j));
  }
}
// What the preview would show if this colour were tapped now (respects the Main / Second tab).
function prefetchColour(code) {
  const c = COLORS.find(x => x.code === code);
  if (!c) return;
  const p = selectionParts();
  let mainName = p.mainName, secName = p.secName;
  const slot = state.colors[0] ? state.colorSlot : 'main';
  if (slot === 'main') { mainName = c.name; if (!state.colors[1]) secName = c.name; }
  else { secName = c.name; }
  if (comboAvailable(mainName, secName, p.pattern)) prefetch(renderUrl(p.pattern, mainName, secName, p.view, p.jump), true);
}
function prefetchPattern(pattern) {
  const p = selectionParts();
  if (comboAvailable(p.mainName, p.secName, pattern)) prefetch(renderUrl(pattern, p.mainName, p.secName, p.view, p.jump), true);
}
// Hover (desktop) or first touch (phone) on a swatch or pattern card starts its download before the tap completes.
function _intentHandler(e) {
  const sw = e.target.closest && e.target.closest('[data-color-code]');
  if (sw) return prefetchColour(sw.dataset.colorCode);
  const pc = e.target.closest && e.target.closest('[data-pattern]');
  if (pc) prefetchPattern(pc.dataset.pattern);
}
document.addEventListener('pointerover', _intentHandler, { passive: true });
document.addEventListener('pointerdown', _intentHandler, { passive: true });
document.addEventListener('touchstart', _intentHandler, { passive: true });
// Desktop with a decent connection: warm the current view of all solid colours while idle.
function prefetchSolidsWhenIdle() {
  const conn = navigator.connection || {};
  const fast = !conn.saveData && !/(^|-)2g$/.test(conn.effectiveType || '');
  if (!fast || !window.matchMedia || !window.matchMedia('(pointer: fine) and (min-width: 861px)').matches) return;
  const run = () => {
    const p = selectionParts();
    COLORS.forEach(c => { if (comboAvailable(c.name, c.name, p.pattern)) prefetch(renderUrl(p.pattern, c.name, c.name, p.view, p.jump)); });
  };
  (window.requestIdleCallback || (f => setTimeout(f, 2500)))(run, { timeout: 4000 });
}

// Generic fallback image when a selection has no render (should not happen now both patterns are complete).
const FALLBACK_PREVIEW = window.FCC_DESIGNER ? window.FCC_DESIGNER.assetBase + 'fcc-seat-full.png' : 'seat-full.png';

// The new image is downloaded AND decoded off-screen before it replaces the old one (Jesse, 2026-09-28: on a phone
// the preview sits behind the edit sheet, and Safari only decoded the new image once the preview was revealed, so the
// old layout showed for about 0.2s). Until it is ready the old image is dimmed (desktop) or hidden (phone), and a
// newer selection always wins over a slower, older one.
let _previewToken = 0;
function renderPreview() {
  const src = previewSrc() || FALLBACK_PREVIEW;
  const token = ++_previewToken;
  const els = ['d-preview-img', 'm-preview-img', 'm-split-img'].map(id => document.getElementById(id)).filter(Boolean);
  els.forEach(el => { el.style.display = ''; }); // undo any earlier onerror hide
  if (els.every(el => el.getAttribute('src') === src)) { els.forEach(el => el.classList.remove('is-loading')); prefetchOtherViews(); return; }
  const probe = new Image();
  probe.decoding = 'async';
  probe.src = src;
  const swap = () => {
    if (token !== _previewToken) return; // a newer choice has taken over
    els.forEach(el => { if (el.getAttribute('src') !== src) el.setAttribute('src', src); el.classList.remove('is-loading'); });
  };
  if (!probe.complete) els.forEach(el => el.classList.add('is-loading'));
  if (probe.decode) probe.decode().then(swap, swap); // on a decode/load error still swap, so the img's onerror applies
  else if (probe.complete) swap();
  else { probe.onload = swap; probe.onerror = swap; }
  prefetchOtherViews();
}

function calcTotal() {
  const base = state.orderType ? ORDER_TYPES[state.orderType].price : 0;
  const extra = state.addExtraSeats ? state.extraSeatQty * EXTRA_SEAT_PRICE : 0;
  const jump = state.addJumpSeat ? JUMP_SEAT_PRICE : 0;
  return base + extra + jump;
}

function colorSummary() {
  if (state.colors.length === 0) return 'Not selected';
  if (state.colors.length === 1) return `${state.colors[0].name} (${state.colors[0].code})`;
  return `${state.colors[0].name} & ${state.colors[1].name} (${state.colors[0].code} & ${state.colors[1].code})`;
}

function vehicleSummary() {
  const v = state.vehicle;
  if (!v.year.trim() && !v.make.trim() && !v.model.trim()) return 'Not selected';
  return `${v.year} ${v.make} ${v.model}${v.trim ? ' ' + v.trim : ''}`.replace(/\s+/g, ' ').trim();
}

/* ============================================================
   VALIDATION — every required choice must be made explicitly;
   nothing is pre-filled or defaulted. Returns { sectionId: message }.
   ============================================================ */
function validateAll() {
  const errors = {};
  const v = state.vehicle;
  if (!v.year.trim() || !v.make.trim() || !v.model.trim()) {
    errors.vehicle = "Please choose your vehicle's make, model and year.";
  } else if (!state.verify.method) {
    errors.vehicle = 'Please choose how you would like us to verify your seats.';
  } else if (state.verify.method === 'config' && !v.frontSeats.trim()) {
    errors.vehicle = 'Please choose your front seat configuration.';
  } else if (state.verify.method === 'vin' && !state.verify.vin.trim()) {
    errors.vehicle = 'Please enter your VIN.';
  }
  if (!state.orderType) {
    errors.coverage = 'Please select a coverage option.';
  }
  if (!state.pattern) {
    errors.pattern = 'Please select a pattern.';
  }
  if (state.colors.length === 0) {
    errors.color = 'Please select at least one color.';
  }
  return errors;
}

/* ============================================================
   ICONS (shared with the design canvas so the two stay visually identical)
   ============================================================ */
const ICONS = {
  vehicle: `<path d="M3 12L5 6h14l2 6M3 12v6a1 1 0 001 1h1a1 1 0 001-1v-1h12v1a1 1 0 001 1h1a1 1 0 001-1v-6M3 12h18M7 15h.01M17 15h.01" stroke="{c}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`,
  coverage: `<rect x="4" y="9" width="16" height="9" rx="2" stroke="{c}" stroke-width="1.6"/><path d="M8 9V6a4 4 0 018 0v3" stroke="{c}" stroke-width="1.6"/>`,
  extraRow: `<path d="M4 18v-4a2 2 0 012-2h12a2 2 0 012 2v4M4 18h16M4 18v1a1 1 0 001 1h1a1 1 0 001-1v-1M18 18v1a1 1 0 001 1h1a1 1 0 001-1v-1" stroke="{c}" stroke-width="1.6" stroke-linecap="round"/>`,
  pattern: `<path d="M12 3l2.5 5 5.5.7-4 3.9.9 5.4-4.9-2.6-4.9 2.6.9-5.4-4-3.9 5.5-.7z" stroke="{c}" stroke-width="1.4" stroke-linejoin="round"/>`,
  paintBucket: `<path d="M19 11L11 3 2.4 11.6a2 2 0 000 2.8l5.2 5.2a2 2 0 002.8 0L19 11z" stroke="{c}" stroke-width="1.6" stroke-linejoin="round"/><path d="M5 2l5 5" stroke="{c}" stroke-width="1.6" stroke-linecap="round"/><path d="M2 13h15" stroke="{c}" stroke-width="1.6" stroke-linecap="round"/><path d="M22 20a2 2 0 11-4 0c0-1.6 1.7-2.4 2-4 .3 1.6 2 2.4 2 4z" stroke="{c}" stroke-width="1.6"/>`,
  chevron: `<path d="M6 9l6 6 6-6" stroke="{c}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`
};
function icon(name, color, size) {
  return `<svg class="acc-icon" style="${size ? `width:${size}px;height:${size}px;` : ''}" viewBox="0 0 24 24" fill="none">${ICONS[name].replace(/\{c\}/g, color)}</svg>`;
}
function chevron() {
  return `<svg class="acc-chevron" viewBox="0 0 24 24" fill="none">${ICONS.chevron.replace(/\{c\}/g, '#6b6455')}</svg>`;
}

/* ============================================================
   SECTION DEFINITIONS — each renders its own collapsed summary
   + expanded body, and wires its own interactive controls.
   ============================================================ */
/* ===== Vehicle database =====
   Same source as the live product-page configurator: FCC Dashboard /api/vehicles/seats (vehicles rows are
   [make, model, [years], [front template idx], [second-row idx], [third-row idx]]; templates are
   [row, label, icon file, integrated seatbelts]). vehicles.js ships a snapshot so the dropdowns fill instantly;
   the live API (about 5s, 480 KB) then refreshes it in the background and is cached in localStorage for next time. */
const VEHICLE_API = 'https://fcc-dashboard.vercel.app/api/vehicles/seats?v=3';
let VEHICLE_DB = null;
function buildVehicleDb(raw) {
  if (!raw || !raw.templates || !raw.vehicles) return null;
  const host = raw.hostUrl || 'https://fcc-dashboard.vercel.app';
  const templates = raw.templates.map(t => ({ label: t[1], icon: `${host}/images/seat-icons/${t[2]}?v=3`, integrated: !!t[3] }));
  const index = {};
  // The database spells some makes two ways, which split one brand's models across two dropdown entries.
  const MAKE_ALIASES = { 'Mercedes': 'Mercedes-Benz', 'Mini': 'MINI', 'Smart Car': 'Smart', 'dodge Ram': 'Ram', 'Infiniti': 'INFINITI', 'Rolls Royce': 'Rolls-Royce' };
  raw.vehicles.forEach(([rawMake, model, years, first, second]) => {
    const make = MAKE_ALIASES[rawMake] || rawMake;
    const mk = index[make] || (index[make] = {});
    const md = mk[model] || (mk[model] = {});
    years.forEach(y => {
      const e = md[y] || (md[y] = { front: new Set(), back: new Set() });
      (first || []).forEach(i => e.front.add(i));
      (second || []).forEach(i => e.back.add(i));
    });
  });
  return { templates, index };
}
function setVehicleDb(raw) {
  const db = buildVehicleDb(raw);
  if (!db) return;
  VEHICLE_DB = db;
  // Refresh the open vehicle section, but never while the customer is typing in it.
  const typing = document.activeElement && /INPUT|TEXTAREA/.test(document.activeElement.tagName);
  if (typeof renderAll === 'function' && document.getElementById('d-accordion') && !typing) renderAll();
}
function loadVehicleDb() {
  let cached = null;
  try { cached = JSON.parse(localStorage.getItem('fcc_vehicles_v3') || 'null'); } catch (e) {}
  const first = (cached && cached.data) || window.FCC_VEHICLES_SNAPSHOT;
  if (first) VEHICLE_DB = buildVehicleDb(first);
  fetch(VEHICLE_API).then(r => r.ok ? r.json() : null).then(d => {
    if (!d || !d.templates || !d.vehicles) return;
    try { localStorage.setItem('fcc_vehicles_v3', JSON.stringify({ t: Date.now(), data: d })); } catch (e) {}
    setVehicleDb(d);
  }).catch(() => {});
}
loadVehicleDb();
// vehicles.js loads async (it is the biggest file and must not hold up the first screen). If it arrives after this
// script, use it unless the cached copy or the live API already filled the list.
window.fccVehiclesLoaded = () => { if (!VEHICLE_DB && window.FCC_VEHICLES_SNAPSHOT) setVehicleDb(window.FCC_VEHICLES_SNAPSHOT); };
const vehicleMakes = () => VEHICLE_DB ? Object.keys(VEHICLE_DB.index).sort((a, b) => a.localeCompare(b)) : [];
const vehicleModels = make => (VEHICLE_DB && VEHICLE_DB.index[make]) ? Object.keys(VEHICLE_DB.index[make]).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })) : [];
const vehicleYears = (make, model) => (VEHICLE_DB && VEHICLE_DB.index[make] && VEHICLE_DB.index[make][model]) ? Object.keys(VEHICLE_DB.index[make][model]).sort((a, b) => b - a) : [];
function seatOptions(which) {
  const v = state.vehicle;
  const e = VEHICLE_DB && VEHICLE_DB.index[v.make] && VEHICLE_DB.index[v.make][v.model] && VEHICLE_DB.index[v.make][v.model][v.year];
  if (!e) return [];
  return [...(which === 'front' ? e.front : e.back)].map(i => VEHICLE_DB.templates[i]).filter(Boolean);
}
// Seat layout picker: icon cards for the chosen vehicle; a text box when typing a vehicle or none are on file.
function seatConfigBlock(which, label, manual) {
  const key = which === 'front' ? 'frontSeats' : 'backSeats';
  const val = state.vehicle[key];
  const esc = s => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  if (manual) return `<div class="field"><label>${label}</label><input data-field="${key}" value="${esc(val)}"></div>`;
  const v = state.vehicle;
  if (!v.year) return `<div class="field"><label>${label}</label><p class="seat-hint">Choose your make, model and year to see the ${which} seat layouts.</p></div>`;
  const list = seatOptions(which);
  if (!list.length) return `<div class="field"><label>${label}</label><p class="seat-hint">No ${which} seat layouts on file for this vehicle yet. Describe it here if you like:</p><input data-field="${key}" value="${esc(val)}"></div>`;
  // Some vehicles list two different layouts under the same name (2017-2023 Tacoma rear, Ram 1500 rear), so the pick is
  // kept by position as well as name, and only one card lights up (Jesse, 2026-10-28).
  const at = list[v[key + 'At']] && list[v[key + 'At']].label === val ? v[key + 'At'] : list.findIndex(o => o.label === val);
  return `<div class="field"><label>${label}</label><div class="seat-grid">${list.map((o, i) => `
    <button type="button" class="seat-card${i === at ? ' selected' : ''}" data-seat-pick="${which}" data-label="${esc(o.label)}" data-at="${i}">
      <img src="${o.icon}" alt="" loading="lazy" decoding="async">
      <span class="seat-card-title">${esc(o.label)}</span>
      <span class="seat-card-desc">${o.integrated ? 'Integrated seatbelts' : 'Standard seatbelts'}</span>
    </button>`).join('')}</div></div>`;
}

/* 40/20/40 front seat -> middle jump seat (Jesse, 2026-09-27). A 40/20/40 front bench has a middle seat, so with Fronts
   Only or Full Set coverage the jump seat switches on automatically. A customer who turns it off keeps it off; switching to a
   non-40/20/40 front seat removes it only if it was switched on automatically. */
const isFortyTwentyForty = label => /40\s*\/\s*20\s*\/\s*40/.test(label || '');
/* ============================================================
   SEAT VERIFICATION (Jesse, 2026-09-28): many customers don't know their seat configuration, so Vehicle Details asks how
   they want us to verify their seats. Photos upload straight away to the dashboard (fcc-dashboard /api/designer/photos)
   into a holding folder keyed by a random id; the id rides on the order as "_Photo Upload ID" and the order webhook
   moves the photos into the customer's portal order.
   ============================================================ */
const VERIFY_OPTIONS = [
  { id: 'photos', label: 'I want to send photos of my seats' },
  { id: 'config', label: 'I want to choose my seat configuration' },
  { id: 'vin', label: 'I want to give you my VIN' }
];
const PHOTO_API = window.FCC_PHOTO_API || 'https://fcc-dashboard.vercel.app/api/designer/photos';
const MAX_PHOTOS = 10;
const SECOND_LAYER_NOTE = 'For a second layer of verification you will still need to send us photos after your purchase.';
const vinLooksWrong = vin => { const s = vin.trim(); return s.length > 0 && (s.length !== 17 || /[^A-HJ-NPR-Z0-9]/i.test(s)); };

function verifyBlock(manual, err) {
  const vf = state.verify;
  const esc = s => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  const missing = err && !vf.method;
  let detail = '';
  if (vf.method === 'photos') {
    const full = vf.photos.length >= MAX_PHOTOS;
    const count = vf.photos.length;
    const camIcon = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V9a1 1 0 011-1z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><circle cx="12" cy="13.5" r="3.5" stroke="currentColor" stroke-width="1.7"/></svg>';
    const galIcon = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" stroke-width="1.7"/><circle cx="8.5" cy="9.5" r="1.8" stroke="currentColor" stroke-width="1.5"/><path d="M4 18l5-5 4 4 3-3 4 4" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>';
    // Tapping the box asks how to add them (Jesse, 2026-09-30: phones went straight to the photo library with no
    // camera option). "Take a photo" opens the camera directly; "Choose from my photos" allows several at once.
    detail = full ? `
      <div class="photo-drop disabled">${camIcon}
        <span class="photo-drop-title">${MAX_PHOTOS} of ${MAX_PHOTOS} photos added</span>
        <span class="photo-drop-sub">Remove a photo to add a different one</span>
      </div>` : vf.pickerOpen ? `
      <div class="photo-drop open">
        <span class="photo-drop-title">How would you like to add photos?</span>
        <div class="photo-choice">
          <label class="photo-choice-btn">${camIcon}<span>Take a photo</span><input type="file" accept="image/*" capture="environment" data-photo-input></label>
          <label class="photo-choice-btn">${galIcon}<span>Choose from my photos</span><input type="file" accept="image/*" multiple data-photo-input></label>
        </div>
        <span class="photo-drop-sub">${count ? `${count} of ${MAX_PHOTOS} added` : `Up to ${MAX_PHOTOS} photos`}</span>
      </div>` : `
      <button type="button" class="photo-drop" data-photo-open>${camIcon}
        <span class="photo-drop-title">${count ? 'Add more photos' : 'Tap to add photos of your seats'}</span>
        <span class="photo-drop-sub">${count ? `${count} of ${MAX_PHOTOS} added` : `Up to ${MAX_PHOTOS} photos: front seats, back seats, and anything unusual`}</span>
      </button>`;
    detail += `
      ${vf.photos.length ? `<div class="photo-thumbs">${vf.photos.map(p => `
        <div class="photo-thumb ${p.status}">
          ${p.thumb ? `<img src="${p.thumb}" alt="">` : ''}
          ${p.status === 'uploading' ? '<span class="photo-status">Uploading</span>' : ''}
          ${p.status === 'error' ? `<span class="photo-status">${esc(p.error || 'Failed')}</span>` : ''}
          <button type="button" class="photo-remove" data-photo-remove="${p.key}" aria-label="Remove photo">&times;</button>
        </div>`).join('')}</div>` : ''}
      <p class="section-note verify-note">If you do not have photos yet, you may upload them after your purchase. We will send you an email with steps to upload.</p>`;
  } else if (vf.method === 'config') {
    detail = `
      ${seatConfigBlock('front', 'Front Seat Configuration', manual)}
      ${seatConfigBlock('back', 'Back Seat Configuration (optional)', manual)}
      <p class="section-note verify-note">${SECOND_LAYER_NOTE}</p>`;
  } else if (vf.method === 'vin') {
    detail = `
      <div class="field"><label>Enter your VIN below</label>
        <input class="vin-input${err && !vf.vin.trim() ? ' field-error' : ''}" data-vin maxlength="17" autocomplete="off" autocapitalize="characters" spellcheck="false" value="${esc(vf.vin)}">
        <p class="vin-warn"${vinLooksWrong(vf.vin) ? '' : ' hidden'}>A VIN is usually 17 letters and numbers. Please double-check it.</p>
      </div>
      <p class="section-note verify-note">${SECOND_LAYER_NOTE}</p>`;
  }
  return `
    <div class="field verify-field"><label>How would you like us to verify your seats?</label>
      <div class="verify-opts${missing ? ' missing' : ''}">${VERIFY_OPTIONS.map(o => `
        <div class="radio-opt ${vf.method === o.id ? 'selected' : ''}" data-verify="${o.id}" role="radio" aria-checked="${vf.method === o.id}" tabindex="0">
          <div class="radio-opt-left"><div class="radio-dot"></div><div class="radio-name">${o.label}</div></div>
        </div>`).join('')}
      </div>
    </div>
    ${detail}`;
}

// iPhone HEIC photos: Chrome and Edge can't show them and the dashboard stores JPEG, so convert in the browser first
// (heic2any, loaded only when needed; the portal's uploader does the same). Then shrink to 1600px JPEG for 4G uploads.
let heicLib = null;
function loadHeic() {
  if (window.heic2any) return Promise.resolve(window.heic2any);
  if (!heicLib) heicLib = new Promise((res, rej) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';
    s.onload = () => res(window.heic2any); s.onerror = () => rej(new Error('Could not read that photo'));
    document.head.appendChild(s);
  });
  return heicLib;
}
async function preparePhoto(file) {
  let blob = file;
  if (/heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name)) {
    const heic2any = await loadHeic();
    const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    blob = Array.isArray(out) ? out[0] : out;
  }
  try {
    const bmp = await createImageBitmap(blob, { imageOrientation: 'from-image' });
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const c = document.createElement('canvas');
    c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
    c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
    const jpeg = await new Promise(r => c.toBlob(r, 'image/jpeg', 0.85));
    if (jpeg) return jpeg;
  } catch (e) { /* fall through: send as is if the browser can't decode it */ }
  if (!/^image\/(jpeg|png|webp)$/.test(blob.type)) throw new Error('Could not read that photo');
  return blob;
}
function newUploadId() {
  if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => { const r = Math.random() * 16 | 0; return (ch === 'x' ? r : (r & 3 | 8)).toString(16); });
}
// Uploads run one at a time on this chain; Order Now waits for it so no photo is cut off by the jump to checkout.
let photoQueue = Promise.resolve();
function addPhotos(files) {
  const vf = state.verify;
  if (!vf.uploadId) vf.uploadId = newUploadId();
  const room = MAX_PHOTOS - vf.photos.length;
  Array.from(files).slice(0, Math.max(0, room)).forEach(file => {
    const entry = { key: Math.random().toString(36).slice(2, 10), thumb: '', status: 'uploading', name: '', error: '' };
    if (!/heic|heif/i.test(file.type + file.name)) entry.thumb = URL.createObjectURL(file);
    vf.photos.push(entry);
    photoQueue = photoQueue.then(async () => {
      if (!vf.photos.includes(entry)) return; // removed before its turn
      try {
        const jpeg = await preparePhoto(file);
        if (!entry.thumb) entry.thumb = URL.createObjectURL(jpeg);
        const fd = new FormData();
        fd.append('uploadId', vf.uploadId);
        fd.append('file', jpeg, 'seat.jpg');
        const res = await fetch(PHOTO_API, { method: 'POST', body: fd });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.success) throw new Error(body.error || 'Upload failed');
        entry.name = body.name; entry.status = 'done';
        if (!vf.photos.includes(entry)) removeUploaded(entry); // removed while it was uploading
      } catch (e) {
        entry.status = 'error'; entry.error = e.message && e.message.length < 40 ? e.message : 'Upload failed';
      }
      renderAll();
    });
  });
  renderAll();
}
function removeUploaded(entry) {
  if (entry.status !== 'done' || !entry.name) return;
  fetch(PHOTO_API, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ uploadId: state.verify.uploadId, name: entry.name }) }).catch(() => {});
}
function removePhoto(key) {
  const vf = state.verify;
  const entry = vf.photos.find(p => p.key === key);
  if (!entry) return;
  vf.photos = vf.photos.filter(p => p !== entry);
  removeUploaded(entry);
  if (entry.thumb) setTimeout(() => URL.revokeObjectURL(entry.thumb), 1000);
  renderAll();
}
const uploadedPhotoCount = () => state.verify.photos.filter(p => p.status === 'done').length;

// The front layout only counts while "choose my seat configuration" is the verification method.
const chosenFrontSeats = () => state.verify.method === 'config' ? state.vehicle.frontSeats : '';
function syncJumpSeat() {
  const frontCoverage = state.orderType === 'Fronts Only' || state.orderType === 'Full Set (Front + Back)';
  if (isFortyTwentyForty(chosenFrontSeats()) && frontCoverage && !state.jumpUserOff) {
    if (!state.addJumpSeat) { state.addJumpSeat = true; state.jumpAuto = true; }
  } else if (state.jumpAuto && !isFortyTwentyForty(chosenFrontSeats())) {
    state.addJumpSeat = false; state.jumpAuto = false;
  }
}

const SECTIONS = [
  {
    id: 'vehicle', label: 'Vehicle Details', iconName: 'vehicle',
    summary: () => vehicleSummary(),
    body: () => {
      const err = state.errors.vehicle;
      const v = state.vehicle;
      const bad = (field) => err && !v[field].trim() ? 'field-error' : '';
      const esc = s => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
      const opts = (list, sel, placeholder) => `<option value="">${placeholder}</option>` +
        list.map(x => `<option value="${esc(x)}"${String(x) === String(sel) ? ' selected' : ''}>${esc(x)}</option>`).join('');
      // Vehicle not in the database (or database not reachable): plain text fields, same as before.
      const manual = v.notListed || !VEHICLE_DB;
      const makeModelYear = manual ? `
        ${VEHICLE_DB ? `<p class="section-note">Type your vehicle below. <button type="button" class="link-btn" data-vehicle-back>Back to the vehicle list</button></p>` : ''}
        <div class="field"><label>Make</label><input class="${bad('make')}" data-field="make" value="${esc(v.make)}"></div>
        <div class="field"><label>Model</label><input class="${bad('model')}" data-field="model" value="${esc(v.model)}"></div>
        <div class="field"><label>Year</label><input class="${bad('year')}" data-field="year" inputmode="numeric" value="${esc(v.year)}"></div>` : `
        <div class="field"><label>Make</label><select class="${bad('make')}" data-select="make">${opts(vehicleMakes(), v.make, 'Select make')}<option value="__other">My vehicle isn't listed</option></select></div>
        <div class="field"><label>Model</label><select class="${bad('model')}" data-select="model" ${v.make ? '' : 'disabled'}>${opts(vehicleModels(v.make), v.model, v.make ? 'Select model' : 'Select a make first')}</select></div>
        <div class="field"><label>Year</label><select class="${bad('year')}" data-select="year" ${v.model ? '' : 'disabled'}>${opts(vehicleYears(v.make, v.model), v.year, v.model ? 'Select year' : 'Select a model first')}</select></div>`;
      return `
      ${err ? `<p class="field-error-note">${err}</p>` : ''}
      ${makeModelYear}
      <div class="field"><label>Trim (optional)</label><input data-field="trim" value="${esc(v.trim)}"></div>
      ${verifyBlock(manual, err)}`;
    },
    wire: (root) => {
      root.querySelectorAll('[data-select]').forEach(sel => {
        sel.addEventListener('change', e => {
          const f = e.target.dataset.select, val = e.target.value;
          const v = state.vehicle;
          if (f === 'make' && val === '__other') {
            Object.assign(v, { notListed: true, make: '', model: '', year: '', frontSeats: '', backSeats: '' });
          } else if (f === 'make') {
            Object.assign(v, { make: val, model: '', year: '', frontSeats: '', backSeats: '' });
          } else if (f === 'model') {
            Object.assign(v, { model: val, year: '', frontSeats: '', backSeats: '' });
          } else if (f === 'year') {
            Object.assign(v, { year: val, frontSeats: '', backSeats: '' });
          }
          if (!validateAll().vehicle) delete state.errors.vehicle;
          syncJumpSeat();
          renderAll();
        });
      });
      const back = root.querySelector('[data-vehicle-back]');
      if (back) back.addEventListener('click', () => {
        Object.assign(state.vehicle, { notListed: false, make: '', model: '', year: '', frontSeats: '', backSeats: '' });
        renderAll();
      });
      root.querySelectorAll('[data-verify]').forEach(opt => {
        const pick = () => {
          state.verify.method = opt.dataset.verify;
          syncJumpSeat(); // a 40/20/40 layout only counts under "choose my seat configuration"
          if (state.errors.vehicle && !validateAll().vehicle) delete state.errors.vehicle;
          renderAll();
        };
        opt.addEventListener('click', pick);
        opt.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } });
      });
      const photoOpen = root.querySelector('[data-photo-open]');
      if (photoOpen) photoOpen.addEventListener('click', e => { e.preventDefault(); state.verify.pickerOpen = true; renderAll(); });
      root.querySelectorAll('[data-photo-input]').forEach(inp => inp.addEventListener('change', e => { if (e.target.files && e.target.files.length) addPhotos(e.target.files); }));
      root.querySelectorAll('[data-photo-remove]').forEach(b => b.addEventListener('click', e => { e.preventDefault(); removePhoto(b.dataset.photoRemove); }));
      const vinInput = root.querySelector('[data-vin]');
      if (vinInput) vinInput.addEventListener('input', e => {
        state.verify.vin = e.target.value.toUpperCase().replace(/\s+/g, '');
        if (e.target.value !== state.verify.vin) e.target.value = state.verify.vin;
        const warn = root.querySelector('.vin-warn');
        if (warn) warn.hidden = !vinLooksWrong(state.verify.vin);
        if (state.verify.vin) e.target.classList.remove('field-error');
        if (state.errors.vehicle && !validateAll().vehicle) { delete state.errors.vehicle; const n = root.querySelector('.field-error-note'); if (n) n.remove(); renderErrorSummary(); }
        renderPeekButton();
      });
      root.querySelectorAll('[data-seat-pick]').forEach(card => {
        card.addEventListener('click', () => {
          const key = card.dataset.seatPick === 'front' ? 'frontSeats' : 'backSeats';
          const label = card.dataset.label, at = Number(card.dataset.at);
          const again = state.vehicle[key] === label && state.vehicle[key + 'At'] === at; // tap the picked card again to clear
          state.vehicle[key] = again ? '' : label;
          state.vehicle[key + 'At'] = again ? -1 : at;
          if (key === 'frontSeats') { state.jumpUserOff = false; syncJumpSeat(); }
          if (state.errors.vehicle && !validateAll().vehicle) delete state.errors.vehicle;
          renderAll();
        });
      });
      root.querySelectorAll('[data-field]').forEach(inp => {
        inp.addEventListener('input', e => {
          const field = e.target.dataset.field;
          state.vehicle[field] = e.target.value;
          if (field === 'frontSeats') syncJumpSeat();
          if (['year', 'make', 'model'].includes(field) && e.target.value.trim()) {
            e.target.classList.remove('field-error');
          }
          if (!validateAll().vehicle) {
            delete state.errors.vehicle;
            const note = root.querySelector('.field-error-note');
            if (note) note.remove();
            renderErrorSummary();
          }
          renderSummaryOnly();
        });
      });
    }
  },
  {
    id: 'coverage', label: 'Coverage', iconName: 'coverage',
    summary: () => state.orderType ? `${state.orderType} · $${ORDER_TYPES[state.orderType].price}` : 'Not selected',
    body: () => `
      ${state.errors.coverage ? `<p class="field-error-note">${state.errors.coverage}</p>` : ''}
      ${Object.keys(ORDER_TYPES).map(key => `
      <div class="radio-opt ${state.orderType === key ? 'selected' : ''}" data-order-type="${key}">
        <div class="radio-opt-left"><div class="radio-dot"></div><div class="radio-name">${key}</div></div>
        <div class="radio-price">$${ORDER_TYPES[key].price}</div>
      </div>`).join('')}`,
    wire: (root) => {
      root.querySelectorAll('[data-order-type]').forEach(el => {
        el.addEventListener('click', () => { state.orderType = el.dataset.orderType; if (state.orderType === 'Backs Only') { state.addJumpSeat = false; state.jumpAuto = false; } else syncJumpSeat(); delete state.errors.coverage; renderAll(); });
      });
    }
  },
  {
    id: 'extraSeats', label: 'Extra Seats', iconName: 'extraRow',
    summary: () => {
      const parts = [];
      if (state.addExtraSeats) parts.push(`${state.extraSeatQty} extra seat${state.extraSeatQty > 1 ? 's' : ''}`);
      if (state.addJumpSeat) parts.push('Front middle jump seat');
      return parts.length ? `Yes · ${parts.join(' · ')}` : 'None';
    },
    body: () => `
      <div class="stepper-row${state.orderType === 'Backs Only' ? ' disabled' : ''}">
        <div class="radio-name">Add Front Middle Jump Seat <span style="color:var(--cream-dim);font-weight:400;">(+$${JUMP_SEAT_PRICE})</span></div>
        <div class="toggle ${state.addJumpSeat ? 'on' : ''}" id="jump-toggle"></div>
      </div>
      ${state.orderType === 'Backs Only' ? '<p class="section-note">The jump seat sits in the front row, so it comes with Full Set or Fronts Only coverage.</p>' : ''}
      <p class="section-note">If you have more than 2 rows, we charge per extra seat. Add your extra seats here.</p>
      <div class="stepper-row">
        <div class="radio-name">Add Extra Seats <span style="color:var(--cream-dim);font-weight:400;">(+$${EXTRA_SEAT_PRICE} each)</span></div>
        <div class="toggle ${state.addExtraSeats ? 'on' : ''}" id="extra-toggle"></div>
      </div>
      ${state.addExtraSeats ? `
      <div class="stepper-row">
        <div class="radio-name" style="font-weight:400;color:var(--cream-dim);">Quantity</div>
        <div class="stepper">
          <button id="extra-minus">−</button><span>${state.extraSeatQty}</span><button id="extra-plus">+</button>
        </div>
      </div>` : ''}`,
    wire: (root) => {
      const t = root.querySelector('#extra-toggle');
      if (t) t.addEventListener('click', () => { state.addExtraSeats = !state.addExtraSeats; renderAll(); });
      const j = root.querySelector('#jump-toggle');
      if (j) j.addEventListener('click', () => { if (state.orderType === 'Backs Only') return; state.addJumpSeat = !state.addJumpSeat; state.jumpAuto = false; state.jumpUserOff = !state.addJumpSeat; renderAll(); });
      const minus = root.querySelector('#extra-minus');
      const plus = root.querySelector('#extra-plus');
      if (minus) minus.addEventListener('click', () => { state.extraSeatQty = Math.max(1, state.extraSeatQty - 1); renderAll(); });
      if (plus) plus.addEventListener('click', () => { state.extraSeatQty += 1; renderAll(); });
    }
  },
  {
    id: 'pattern', label: 'Pattern', iconName: 'pattern',
    summary: () => state.pattern || 'Not selected',
    body: () => `
      ${state.errors.pattern ? `<p class="field-error-note">${state.errors.pattern}</p>` : ''}
      <div class="pattern-grid ${state.errors.pattern ? 'grid-error' : ''}">${PATTERNS.map(p => `
      <div class="pattern-card ${state.pattern === p.id ? 'selected' : ''}" data-pattern="${p.id}">
        <div class="pattern-thumb"><img src="${p.thumb}" alt="${p.name} pattern"></div>
        <div class="pattern-name">${p.name}</div>
        <div class="pattern-check">✓</div>
      </div>`).join('')}</div>`,
    wire: (root) => {
      root.querySelectorAll('[data-pattern]').forEach(el => {
        el.addEventListener('click', () => { state.pattern = el.dataset.pattern; delete state.errors.pattern; renderAll(); });
      });
    }
  },
  {
    id: 'color', label: 'Color', iconName: 'paintBucket',
    summary: () => colorSummary(),
    body: () => {
      const main = state.colors[0], second = state.colors[1];
      const slot = main ? state.colorSlot : 'main'; // Second is only usable once a main exists
      const chip = c => c ? `<span class="slot-chip"><img src="${c.swatchUrl}" alt=""></span>` : '<span class="slot-chip empty"></span>';
      // Customers scrolled the swatches without realising a second colour was possible (Jesse, 2026-10-26): with no second
      // colour, the Second tab is a gold "+ Add a 2nd color" button (pulsing twice after the first main pick), and the
      // hint says up front that two tone exists.
      const note = state.errors.color
        ? `<p class="field-error-note">${state.errors.color}</p>`
        : `<p class="swatch-note">${slot === 'main'
            ? (!main ? 'Pick your main color. You can add a second color for two tone.'
              : second ? 'Tap any color to change the main color. Tap <strong>Second</strong> to change the second color.'
              : 'Tap any color to change the main color. For two tone, tap <strong>Add a 2nd color</strong>.')
            : 'Tap any color to change the second color, or <strong>None</strong> for a single color.'}</p>`;
      const addSecond = !second && slot !== 'second';
      const secondTab = addSecond
        ? `<button class="slot-tab add ${main ? '' : 'locked'} ${main && state.pulseSecond ? 'pulse' : ''}" data-slot="second" role="tab" aria-selected="false" ${main ? '' : 'disabled title="Choose a main color first"'}>
          <span class="slot-plus">+</span>
          <span class="slot-text"><span class="slot-label">Two tone</span><span class="slot-value">Add a 2nd color</span></span>
        </button>`
        : `<button class="slot-tab ${slot === 'second' ? 'active' : ''}" data-slot="second" role="tab" aria-selected="${slot === 'second'}">
          <span class="slot-num">2</span>${chip(second)}
          <span class="slot-text"><span class="slot-label">Second</span><span class="slot-value">${second ? second.name : 'None (solid)'}</span></span>
        </button>`;
      return `
      <div class="slot-tabs" role="tablist">
        <button class="slot-tab ${slot === 'main' ? 'active' : ''}" data-slot="main" role="tab" aria-selected="${slot === 'main'}">
          <span class="slot-num">1</span>${chip(main)}
          <span class="slot-text"><span class="slot-label">Main</span><span class="slot-value">${main ? main.name : 'Choose'}</span></span>
        </button>
        ${secondTab}
      </div>
      ${note}
      <div class="swatch-grid-box ${state.errors.color ? 'grid-error' : ''}"><div class="swatch-grid">
        ${slot === 'second' ? `<button class="swatch-item swatch-none ${second ? '' : 'selected current'}" data-color-none="1" title="No second color (solid)">
            <div class="swatch-dot"><span>None</span></div>
            <div class="swatch-label">Solid</div>
          </button>` : ''}
        ${COLORS.map((c) => {
          const isMain = main && main.code === c.code;
          const isSecond = second && second.code === c.code;
          // The swatch the active tab currently holds gets the strong ring; the other slot's colour keeps its number badge.
          const current = slot === 'main' ? isMain : isSecond;
          const unavailable = slot === 'second' && !isMain && !isSecond && !secondaryAvailable(c.name);
          const title = unavailable ? `${c.name} (${c.code}), no preview yet with ${main.name}` : `${c.name} (${c.code})`;
          return `<button class="swatch-item ${isMain || isSecond ? 'selected' : ''} ${isSecond ? 'second' : ''} ${current ? 'current' : ''} ${unavailable ? 'unavailable' : ''}" data-color-code="${c.code}" ${unavailable ? 'disabled aria-disabled="true"' : ''} title="${title}">
            <div class="swatch-dot"><img src="${c.swatchUrl}" alt="${c.name}"></div>
            <div class="swatch-label">${c.name}</div>
          </button>`;
        }).join('')}
      </div></div>`;
    },
    wire: (root) => {
      root.querySelectorAll('[data-slot]').forEach(el => {
        el.addEventListener('click', () => {
          if (el.dataset.slot === 'second' && !state.colors[0]) return;
          state.colorSlot = el.dataset.slot;
          renderAll();
        });
      });
      const none = root.querySelector('[data-color-none]');
      if (none) none.addEventListener('click', () => { state.colors = state.colors.slice(0, 1); renderAll(); });
      root.querySelectorAll('[data-color-code]').forEach(el => {
        el.addEventListener('click', () => {
          const color = COLORS.find(c => c.code === el.dataset.colorCode);
          const [main, second] = state.colors;
          state.pulseSecond = !main; // the first main pick pulses "+ Add a 2nd color" once (this render only)
          const slot = main ? state.colorSlot : 'main';
          // One tap replaces the colour in the active slot. Picking the same colour for both slots means solid.
          if (slot === 'main') {
            state.colors = second && second.code !== color.code ? [color, second] : [color];
          } else {
            state.colors = color.code === main.code ? [main] : [main, color];
          }
          // Never leave a pair that has no render (keeps preview and greying in agreement).
          if (state.colors.length === 2 && !comboAvailable(state.colors[0].name, state.colors[1].name)) {
            state.colors = [state.colors[0]];
          }
          if (state.colors.length > 0) delete state.errors.color;
          renderAll();
          state.pulseSecond = false;
        });
      });
    }
  }
];

/* ============================================================
   RENDER
   ============================================================ */
function renderAccordion(containerId, compact) {
  const container = document.getElementById(containerId);
  container.innerHTML = SECTIONS.map(s => {
    const open = state.openSection === s.id;
    const invalid = !!state.errors[s.id];
    return `
    <div class="acc-row ${open ? 'open' : ''} ${invalid ? 'invalid' : ''}" data-section="${s.id}">
      <button class="acc-row-head" data-toggle="${s.id}">
        <div class="acc-left">
          ${icon(s.iconName, open ? '#26221a' : '#a97c26', compact ? 17 : null)}
          <div style="min-width:0;">
            <div class="acc-label">${s.label}</div>
            <div class="acc-value">${s.summary()}</div>
          </div>
        </div>
        ${chevron()}
      </button>
      <div class="acc-body"><div class="acc-body-inner" data-body="${s.id}">${open ? s.body() : ''}</div></div>
    </div>`;
  }).join('');

  container.querySelectorAll('[data-toggle]').forEach(el => {
    el.addEventListener('click', () => {
      const id = el.dataset.toggle;
      state.openSection = state.openSection === id ? null : id;
      renderAll();
    });
  });

  const openSection = SECTIONS.find(s => s.id === state.openSection);
  if (openSection) {
    const body = container.querySelector(`[data-body="${openSection.id}"]`);
    if (body) {
      openSection.wire(body);
      // Measure the real rendered height instead of relying on a guessed
      // cap — a fixed max-height (e.g. 600px) silently clips any section
      // whose content ends up taller than that guess (this is exactly
      // what was cropping the bottom of Vehicle Details' inputs).
      const wrapper = body.closest('.acc-body');
      // The inner block's own height, not the wrapper's scrollHeight: the wrapper uses overflow:clip (so the colour tabs
      // can stay pinned), and a clip box is not a scroll container.
      wrapper.style.maxHeight = body.offsetHeight + 'px';
    }
  }
}

function renderSummaryOnly() {
  document.getElementById('d-vehicle-sub').textContent = vehicleSummary();
  document.getElementById('m-vehicle-sub').textContent = vehicleSummary().toUpperCase();
  renderPeekButton();
}

/* The closed phone drawer's button (Jesse, 2026-09-28): an older customer never realised the drawer opened, so while a
   required choice is missing the button names the next step in two words and opens the drawer there. It reads
   Order Now once everything is chosen; inside the open drawer it is always Order Now. */
const NEXT_STEP_LABELS = { vehicle: 'Choose Vehicle', coverage: 'Choose Coverage', pattern: 'Choose Pattern', color: 'Choose Colors' };
const UP_ARROW = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 15l6-6 6 6" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
function nextMissingStep() {
  const missing = Object.keys(validateAll());
  const first = SECTIONS.find(s => missing.includes(s.id));
  return first ? first.id : null;
}
function renderPeekButton() {
  const btn = document.getElementById('m-order-btn-peek');
  if (!btn) return;
  const next = nextMissingStep();
  const v = state.vehicle;
  const vehiclePicked = v.year.trim() && v.make.trim() && v.model.trim();
  const label = next === 'vehicle' && vehiclePicked ? 'Choose Verification' : (NEXT_STEP_LABELS[next] || 'Choose Options');
  const html = next ? `${label} ${UP_ARROW}` : 'Order Now →';
  const shown = next ? label : '';
  if (btn.dataset.shown !== shown || !btn.innerHTML) { btn.innerHTML = html; btn.dataset.shown = shown; }
  renderAgreement(!next);
}

/* Agreement checkbox, copied from the live product page (car-configurator-master.liquid): it appears under the price,
   Affirm line and button only once every required choice is made, and Order Now stays disabled until it is ticked. */
function renderAgreement(complete) {
  ['d', 'm'].forEach(p => {
    const line = document.getElementById(`${p}-agree-line`);
    const box = document.getElementById(`${p}-agree`);
    if (!line || !box) return;
    line.classList.toggle('show', complete);
    box.checked = state.agreed;
  });
  ['d-order-btn', 'm-order-btn'].forEach(id => {
    const b = document.getElementById(id);
    if (b) b.disabled = complete && !state.agreed;
  });
}
function flashAgreement() {
  ['d', 'm'].forEach(p => {
    const line = document.getElementById(`${p}-agree-line`);
    if (!line || line.offsetParent === null) return;
    line.classList.remove('flash'); void line.offsetWidth; line.classList.add('flash');
  });
}

function renderTotals() {
  const total = calcTotal();
  const affirmHtml = state.orderType
    ? `Pay 4 interest-free payments of <strong>$${(total / 4).toFixed(2)}</strong> with <strong>Affirm</strong>`
    : 'Add a coverage option to see Affirm pricing';
  ['d-total', 'm-total', 'm-total-2'].forEach(id => document.getElementById(id).textContent = `$${total}`);
  document.getElementById('d-affirm').innerHTML = affirmHtml;
  document.getElementById('m-affirm').innerHTML = affirmHtml;
  const captionColor = colorSummary().split(' (')[0];
  document.getElementById('d-caption').textContent = state.colors.length || state.pattern
    ? `${captionColor} · ${state.pattern || 'Pattern'}`.toUpperCase()
    : 'SELECT A COLOR & PATTERN';
}

function renderErrorSummary() {
  const ids = Object.keys(state.errors);
  const html = ids.length
    ? `<div class="order-error-banner"><strong>Please complete the following before ordering:</strong><ul>${ids.map(id => `<li>${state.errors[id]}</li>`).join('')}</ul></div>`
    : '';
  document.getElementById('d-error-banner').innerHTML = html;
  document.getElementById('m-error-banner').innerHTML = html;
}

function renderAll() {
  renderSummaryOnly();
  renderTotals();
  renderPreview();
  renderErrorSummary();
  renderAccordion('d-accordion', false);
  renderAccordion('m-accordion', true);
  updateSplitView();
}

/* Colour split view on phones (Jesse, 2026-10-26). Session recordings showed customers swiping up and down at the colour
   step looking for the preview: the open drawer filled 82% of the screen and blurred the rest. While the Colour or
   Pattern section is open (Pattern added the same day, Jesse), the drawer drops to 60% of the screen and the seats show
   above it, changing with every tap. The other sections keep the tall drawer. Skipped on short screens (phones held
   sideways), where there is no room for both. */
const SPLIT_SECTIONS = ['pattern', 'color'];
function updateSplitView() {
  const sheet = document.getElementById('sheet'), overlay = document.getElementById('preview-tap-overlay');
  const img = document.getElementById('m-split-img'), wrap = document.querySelector('.mobile-wrap');
  if (!sheet || !overlay || !img || !wrap) return;
  const top = Math.max(0, Math.round(wrap.getBoundingClientRect().top)); // below the store's announcement bar
  const avail = window.innerHeight - top;
  const on = window.matchMedia('(max-width: 860px)').matches && avail >= 560
    && sheet.classList.contains('expanded') && SPLIT_SECTIONS.includes(state.openSection);
  const was = sheet.dataset.splitFor || '';
  sheet.dataset.splitFor = on ? state.openSection : '';
  sheet.classList.toggle('split', on);
  overlay.classList.toggle('split', on);
  if (!on) { sheet.style.height = ''; overlay.style.top = ''; return; }
  const sheetH = Math.round(Math.max(340, avail * 0.6));
  const stageH = avail - sheetH;
  const size = Math.max(120, Math.min(stageH - 16, window.innerWidth - 24)); // the renders are square
  sheet.style.height = sheetH + 'px';
  overlay.style.top = top + 'px';
  img.style.width = img.style.height = size + 'px';
  img.style.top = Math.round((stageH - size) / 2) + 'px';
  overlay.style.setProperty('--split-mid', Math.round(stageH / 2) + 'px');
  if (was !== state.openSection) {
    // On the way in (or when switching between Pattern and Colour), bring the choices to the top of the drawer once it
    // has shrunk: the colour tabs, or the pattern row.
    setTimeout(() => {
      const list = document.getElementById('m-accordion'), row = list.querySelector('.acc-row.open');
      if (!row || sheet.dataset.splitFor !== row.dataset.section) return;
      const target = row.dataset.section === 'color' ? row.querySelector('.acc-body') : row;
      list.scrollTo({ top: list.scrollTop + target.getBoundingClientRect().top - list.getBoundingClientRect().top - 8, behavior: 'smooth' });
    }, 280);
  }
}

/* ============================================================
   ADD TO CART — a REAL storefront cart add, not a draft order.

   The FCC Dashboard's "Create Order" flow (src/lib/shopify.ts,
   createDraftOrder) hits Shopify's Admin API to build a draft
   order that a sales rep has to open and manually send — that's
   an invoicing tool for reps, and this builder does NOT use it
   or any Admin API. This builder only borrows the dashboard's
   variant IDs and its {name, value} line-item property format
   (see src/app/actions/sales-invoices.ts) so an order placed here
   looks the same in the order view as one a rep built — the
   submission path itself is Shopify's public Storefront AJAX
   Cart API (POST /cart/add.js), the same endpoint every normal
   "Add to Cart" button on the live store uses. A customer who
   clicks Order Now here lands in their own cart and checks out
   themselves; no rep, no draft, no manual invoice send.
   ============================================================ */
function verificationProperty() {
  const m = state.verify.method;
  if (m === 'photos') {
    const n = uploadedPhotoCount();
    return { name: 'Seat Verification', value: n ? `Photos (${n} uploaded)` : 'Photos (sending after purchase)' };
  }
  if (m === 'config') return { name: 'Seat Verification', value: 'Seat Configuration' };
  if (m === 'vin') return { name: 'Seat Verification', value: 'VIN' };
  return null;
}

function buildLineItemProperties() {
  return [
    { name: 'Vehicle', value: vehicleSummary() },
    verificationProperty(),
    // Only the chosen verification method's answers go on the order.
    state.verify.method === 'config' && state.vehicle.frontSeats ? { name: 'Front Seat Config', value: state.vehicle.frontSeats } : null,
    state.verify.method === 'config' && state.vehicle.backSeats ? { name: 'Back Seat Config', value: state.vehicle.backSeats } : null,
    state.verify.method === 'vin' && state.verify.vin.trim() ? { name: 'VIN', value: state.verify.vin.trim() } : null,
    // Leading underscore: Shopify hides it from the cart and checkout, but it still reaches the order webhook.
    state.verify.method === 'photos' && uploadedPhotoCount() ? { name: '_Photo Upload ID', value: state.verify.uploadId } : null,
    { name: 'Order Type', value: state.orderType },
    { name: 'Pattern', value: state.pattern },
    { name: 'Color', value: colorSummary() },
    // Same property and wording as the live product page's configurator, so orders look the same in Shopify.
    state.agreed ? { name: 'Agreement', value: 'Reviewed & Agreed to Refund Policy + Terms of Service' } : null
  ].filter(Boolean);
}

function isShopifyOrigin() {
  if (window.FCC_DESIGNER && window.FCC_DESIGNER.shopify) return true; // set by the theme section on the live store
  const h = window.location.hostname;
  return h.endsWith('.myshopify.com') || document.querySelector('meta[name="shopify-checkout-api-token"]') !== null;
}

async function handleOrderNow() {
  state.errors = validateAll();
  const missingIds = Object.keys(state.errors);
  if (missingIds.length > 0) {
    // Jump to whichever required section comes first in the list, and let
    // the red banner + per-row/per-field highlighting show everything else
    // that still needs attention.
    const firstMissing = SECTIONS.find(s => missingIds.includes(s.id));
    state.openSection = firstMissing ? firstMissing.id : state.openSection;
    renderAll();
    document.querySelectorAll(`.acc-row[data-section="${state.openSection}"]`).forEach(el => {
      if (el.offsetParent !== null) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return;
  }
  if (!state.agreed) { flashAgreement(); return; }
  // Let any photo still uploading finish before the page leaves for checkout.
  if (state.verify.method === 'photos' && state.verify.photos.some(p => p.status === 'uploading')) {
    const btns = ['d-order-btn', 'm-order-btn'].map(id => document.getElementById(id)).filter(Boolean);
    btns.forEach(b => { b.disabled = true; b.textContent = 'Uploading photos…'; });
    await photoQueue;
    btns.forEach(b => { b.disabled = false; b.textContent = 'Order Now →'; });
  }

  // The storefront cart (/cart/add.js) needs properties as a { name: value } object; the [{name, value}] list
  // is the Admin draft-order shape the dashboard uses. The list was rejected live with "expected Array to be a Hash".
  const properties = Object.fromEntries(buildLineItemProperties().map(p => [p.name, p.value]));
  const items = [
    { id: ORDER_TYPES[state.orderType].variantId, quantity: 1, properties }
  ];
  if (state.addExtraSeats) {
    items.push({ id: EXTRA_SEAT_VARIANT_ID, quantity: state.extraSeatQty });
  }
  if (state.addJumpSeat) {
    items.push({ id: JUMP_SEAT_VARIANT_ID, quantity: 1 });
  }

  if (isShopifyOrigin()) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) throw new Error(await res.text());
      // Straight to checkout once everything is filled in (Jesse, 2026-09-28); validateAll() above already stopped
      // the order if anything was missing.
      window.location.href = '/checkout';
    } catch (err) {
      showModal('Add to Cart Failed', 'Something went wrong reaching the Shopify cart.', String(err));
    }
    return;
  }

  showModal(
    'Order Payload (Local Dev — not a live Shopify store)',
    'This page isn\'t running on the live Shopify store, so nothing was actually added to a cart. This is exactly what would be POSTed to /cart/add.js:',
    JSON.stringify({ items }, null, 2)
  );
}

function showModal(title, sub, pre) {
  document.getElementById('modal-title').textContent = title;
  document.getElementById('modal-sub').textContent = sub;
  document.getElementById('modal-pre').textContent = pre;
  document.getElementById('modal').classList.add('show');
}

function initApp() {
  document.getElementById('modal-close').addEventListener('click', () => document.getElementById('modal').classList.remove('show'));
  document.getElementById('modal').addEventListener('click', (e) => { if (e.target.id === 'modal') e.currentTarget.classList.remove('show'); });

  document.getElementById('d-order-btn').addEventListener('click', handleOrderNow);
  document.getElementById('m-order-btn').addEventListener('click', handleOrderNow);

  /* MOBILE SHEET peek/expand. The drawer has to announce itself (Jesse, 2026-09-28): an older customer scrolled the
     preview for a while without realising it opened. The closed drawer's button names the next step (renderPeekButton),
     the drawer nudges upward on arrival, and it opens with a tap or an upward swipe. */
  const sheet = document.getElementById('sheet');
  const previewOverlay = document.getElementById('preview-tap-overlay');
  let sheetOpenedOnce = false;
  function setSheet(expanded) {
    sheet.classList.toggle('peek', !expanded);
    sheet.classList.toggle('expanded', expanded);
    previewOverlay.classList.toggle('show', expanded); // dark tint + "tap to preview" over the exposed image area while expanded
    if (expanded && !sheetOpenedOnce) { sheetOpenedOnce = true; sheet.classList.remove('nudge'); }
    // The open section's height is measured when it renders, and a render while the drawer is closed (its list hidden)
    // measures 0. That left "Choose Colors" opening an empty Color section, live since 2026-09-28 (found 2026-10-26).
    // Measure again now the list is visible.
    if (expanded) {
      const inner = document.querySelector('#m-accordion .acc-row.open .acc-body-inner');
      if (inner) inner.parentElement.style.maxHeight = inner.offsetHeight + 'px';
    }
    updateSplitView();
  }
  document.getElementById('m-order-btn-peek').addEventListener('click', (e) => {
    e.stopPropagation();
    const next = nextMissingStep();
    if (!next && state.agreed) { handleOrderNow(); return; }
    if (!next) {
      // Everything chosen but the agreement is not ticked: open the drawer so the checkbox under Order Now is in view.
      setSheet(true);
      setTimeout(flashAgreement, 300);
      return;
    }
    // Open the drawer on the step that is missing, without the red "please complete" errors of a real order attempt.
    state.openSection = next;
    renderAll();
    setSheet(true);
    const v = state.vehicle;
    // Vehicle already picked but verification missing: land on the verification question, not the top of the section.
    const row = (next === 'vehicle' && v.year.trim() && v.make.trim() && v.model.trim() && document.querySelector('#m-accordion .verify-field'))
      || document.querySelector(`#m-accordion .acc-row[data-section="${next}"]`);
    // Pattern and Colour are left to the split view, which brings their choices into view itself.
    if (row && !SPLIT_SECTIONS.includes(next)) setTimeout(() => row.scrollIntoView({ behavior: 'smooth', block: 'start' }), 260);
  });
  ['d-agree', 'm-agree'].forEach(id => document.getElementById(id).addEventListener('change', (e) => {
    state.agreed = e.target.checked;
    renderPeekButton(); // keeps both checkboxes in step and re-enables Order Now
  }));
  renderPeekButton();
  document.getElementById('sheet-handle').addEventListener('click', () => setSheet(!sheet.classList.contains('expanded')));
  document.getElementById('sheet-peek-row').addEventListener('click', () => setSheet(true));
  previewOverlay.addEventListener('click', () => setSheet(false));

  // Swipe: up anywhere on the closed drawer opens it; down on the open drawer's handle closes it
  // (the open drawer's list scrolls, so only its handle takes a closing swipe).
  let swipeStartY = null;
  sheet.addEventListener('touchstart', (e) => {
    const onHandle = !!e.target.closest('#sheet-handle');
    swipeStartY = (sheet.classList.contains('peek') || onHandle) ? e.touches[0].clientY : null;
  }, { passive: true });
  sheet.addEventListener('touchend', (e) => {
    if (swipeStartY === null) return;
    const dy = e.changedTouches[0].clientY - swipeStartY;
    swipeStartY = null;
    if (dy < -30 && sheet.classList.contains('peek')) setSheet(true);
    else if (dy > 30 && sheet.classList.contains('expanded')) setSheet(false);
  }, { passive: true });

  // One nudge on arrival, phones and tablets only, skipped once the drawer has been opened.
  setTimeout(() => {
    if (sheetOpenedOnce || !window.matchMedia('(max-width: 860px)').matches) return;
    sheet.classList.add('nudge');
    const endNudge = (e) => { if (e.target !== sheet) return; sheet.classList.remove('nudge'); sheet.removeEventListener('animationend', endNudge); };
    sheet.addEventListener('animationend', endNudge);
  }, 1200);

  /* Keep the image clear of the disclaimer card no matter how many lines
     it wraps to on a given screen width — measuring the real rendered
     height beats guessing a fixed offset, which is what let the two
     overlap on narrower phones where the disclaimer text wraps taller. */
  /* The disclaimer sits just under the header (title, vehicle line and star badge) at its real height too, instead of a
     fixed 76px that the late-loading Stamped badge ran into (Jesse, 2026-09-27: uneven spacing). */
  const mobilePreview = document.querySelector('.mobile-preview-hero');
  const mobileDisclaimer = document.getElementById('mobile-disclaimer');
  const mobileHeader = document.querySelector('.mobile-header');
  function positionMobilePreviewImage() {
    const gap = 16;
    // The header's own bottom padding is the gap; take back the disclaimer's margin-top so it isn't doubled.
    const margin = parseFloat(getComputedStyle(mobileDisclaimer).marginTop) || 0;
    mobileDisclaimer.style.top = (mobileHeader.offsetTop + mobileHeader.offsetHeight - margin) + 'px';
    mobilePreview.style.paddingTop = (mobileDisclaimer.offsetTop + mobileDisclaimer.offsetHeight + gap) + 'px';
    fitMobilePreviewImage(gap);
  }
  /* Short phones (iPhone SE and smaller, or any phone under the store's top bar): shrink the seat image so it ends above
     the closed drawer instead of sliding under it, since the drawer grew to carry its call-to-action bar (2026-09-28). */
  const mobileImgWrap = document.querySelector('.mobile-preview-imgwrap');
  const mobileImg = mobileImgWrap && mobileImgWrap.querySelector('img');
  function fitMobilePreviewImage(gap) {
    if (!mobileImgWrap || !mobileImg || !sheet.classList.contains('peek')) return;
    mobileImgWrap.style.maxWidth = '';
    const aspect = (mobileImg.naturalWidth && mobileImg.naturalHeight) ? mobileImg.naturalWidth / mobileImg.naturalHeight : 1.5;
    const scroller = mobilePreview.parentElement;
    const imgTop = scroller.getBoundingClientRect().top - scroller.scrollTop + mobilePreview.offsetTop + parseFloat(mobilePreview.style.paddingTop || 0);
    const drawerTop = window.innerHeight - sheet.offsetHeight;
    const room = drawerTop - imgTop - gap;
    const natural = mobileImgWrap.getBoundingClientRect().width;
    if (room > 0 && natural / aspect > room) mobileImgWrap.style.maxWidth = Math.max(200, Math.floor(room * aspect)) + 'px';
  }
  if (mobileImg) mobileImg.addEventListener('load', positionMobilePreviewImage);
  positionMobilePreviewImage();
  window.addEventListener('resize', positionMobilePreviewImage);
  window.addEventListener('resize', updateSplitView);
  window.addEventListener('orientationchange', positionMobilePreviewImage);
  if ('ResizeObserver' in window) new ResizeObserver(positionMobilePreviewImage).observe(mobileHeader);

  /* Stamped's script only scans for the literal id "stamped-reviews-widget"
     (data-widget-type alone isn't enough — confirmed live) — but it turns
     out duplicates of that exact id each initialize independently, so the
     Site Badge just works as static markup in both the desktop and mobile
     slots (see the comment on it in index.html) without needing any of
     this. The reviews wall widget below is the one exception worth this
     relocate-on-resize dance: at the time this was written it appeared a
     second copy of it silently failed, so rather than risk that again it
     stays a single node moved between layouts instead of duplicated. */
  // The reviews widget, not the star badge: badges share Stamped's 'stamped-reviews-widget' id, so looking that id up
  // returned the desktop badge and phones never got the reviews (fixed 2026-09-27).
  const reviewsWidget = document.getElementById('stamped-main-widget');
  const desktopReviewsSlot = document.querySelector('.desktop-wrap .preview-reviews');
  const mobileReviewsSlot = document.getElementById('mobile-reviews-slot');
  const MOBILE_BREAKPOINT = 860;
  let widgetInMobile = false;
  function relocateReviewsWidget() {
    const shouldBeMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    if (shouldBeMobile && !widgetInMobile) {
      mobileReviewsSlot.appendChild(reviewsWidget);
      widgetInMobile = true;
    } else if (!shouldBeMobile && widgetInMobile) {
      desktopReviewsSlot.appendChild(reviewsWidget);
      widgetInMobile = false;
    }
  }
  relocateReviewsWidget();
  window.addEventListener('resize', relocateReviewsWidget);
  window.addEventListener('orientationchange', relocateReviewsWidget);

  renderAll();
  prefetchSolidsWhenIdle();
}

// Start as soon as this script runs (the designer markup is already above it) instead of waiting for DOMContentLoaded,
// which waits for every store app script and made the sidebar appear 1 to 3 seconds late (Jesse, 2026-09-27).
if (document.getElementById('app')) initApp(); else document.addEventListener('DOMContentLoaded', initApp);

/* FAQ under the preview (Jesse, 2026-09-27): FAQS from data.js as an accordion, one answer open at a time,
   rendered into the desktop and mobile preview columns. The "Scroll for FAQ & Reviews" hint scrolls to it. */
(function renderFaq() {
  if (typeof FAQS === 'undefined') return;
  const html = FAQS.map(f => `
    <details class="faq-item">
      <summary><span>${f.q}</span><svg viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></summary>
      <div class="faq-a">${f.a.map(p => `<p>${p}</p>`).join('')}</div>
    </details>`).join('');
  document.querySelectorAll('.faq-list').forEach(list => {
    list.innerHTML = html;
    list.addEventListener('toggle', e => {
      if (!e.target.open) return;
      list.querySelectorAll('details[open]').forEach(d => { if (d !== e.target) d.open = false; });
    }, true);
  });
  document.querySelectorAll('.scroll-hint').forEach(hint => {
    hint.addEventListener('click', () => {
      const faq = hint.closest('.preview, .mobile-preview')?.querySelector('.preview-faq');
      if (faq) faq.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
})();

/* Reviews visibility: the review area stays hidden until Stamped actually injects reviews, so a lapsed Stamped
   plan (Sept 2026: every widget call returned 401) never shows customers an empty section. It reappears by itself
   the moment Stamped delivers content. Only the reviews widget counts, not the star badge. The FAQ and the scroll
   hint always show. */
(function watchReviews() {
  const app = document.getElementById('app');
  if (!app) return;
  const hasReviews = () => [...document.querySelectorAll('#stamped-main-widget')]
    .some(el => el.children.length > 0 && el.innerText.trim().length > 0);
  const update = () => app.classList.toggle('no-reviews', !hasReviews());
  update();
  new MutationObserver(update).observe(app, { childList: true, subtree: true });
})();
